import React, { useEffect } from "react";
import { compose } from "redux";
import { Icon } from "semantic-ui-react";
import { useTranslation, Trans } from "react-i18next";
import { useHistory } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import {
  Form,
  FormBuilder,
  ActionButton,
  TriggerBuilder,
} from "@redriver/cinnamon";
import BlueAddSvg from "features/../../../shared/assets/blue-add.svg";
import {
  PermissionCheck,
  withCurrentUser,
  withPermissions,
} from "features/../../../shared/components/auth";
import { AddButton } from "features/../../../shared/components/buttons";
import {
  Actions,
  Targets,
} from "features/../../../shared/constants/permissions";
import { DeleteSchedule } from ".";
import { DuplicateFromTemplateModal } from "./DuplicateFromTemplate";
import { RequestPanel } from "../ScheduleRequests";
import { SheetRoutes } from "constants/routes";
import { getAgreementsState } from "../../selectors";
import {
  generateSheet,
  addExpandedAgreementOrSchedule,
  removeExpandedAgreementOrSchedule,
  getAgreementScheduleSheets,
  getMasterAgreement,
  updateSchedule,
} from "../actions";
import { FormattedNumericInput } from "features/../../../shared/components/forms";

const ScheduleFormContent = ({
  formProps,
  state,
  formEvents,
  agreement,
  schedule,
  isAdminUser,
  isInternalUser,
  getExpandIconName,
  canUpdateCreditAvailable,
  companyFilters,
  t,
  history,
  onToggleScheduleExpanded,
  reloadAgreement,
  canDuplicateFromTemplates,
  hasTemplates,
}) => {
  useEffect(() => {
    if (schedule.startDate !== formProps.value.startDate) {
      formEvents.onReset(schedule);
    }
  }, [schedule.startDate, formEvents.onReset]);
  return (
    <Form {...formProps} className="manage-agreement-form">
      <div className="full-width">
        <div style={{ display: "flex" }} className="float-left name">
          <div
            className="spaced expand-collapse"
            onClick={() => onToggleScheduleExpanded(schedule.scheduleId)}
          >
            <Icon name={getExpandIconName(schedule.scheduleId)} />
            {t("ScheduleRow.ProjectName_Label")}
          </div>
          <div className="spaced no-padding-left">
            <Form.Input
              field="projectName"
              className="project-name"
              placeholder={t("ScheduleRow.ProjectName_Placeholder")}
              loading={state.submitting}
              required
              disabled={!isAdminUser}
            />
          </div>
        </div>
        <div className="float-left">
          <RequestPanel schedule={schedule} />
        </div>

        <div className="float-left grid">
          {!isInternalUser || process.env.TRADE_AS_IPS ? (
            <div className="float-left spaced not-bold">
              <Trans
                t={t}
                values={{
                  ref: schedule.scheduleReference ?? t("NotLive"),
                }}
                components={{
                  span: <span />,
                }}
              >
                ScheduleRow.TitleWithRef
              </Trans>
            </div>
          ) : (
            <div className="float-left">
              <div className="float-left spaced not-bold">
                {t("ScheduleRow.Title")}
              </div>
              <div className="float-left spaced no-padding-left">
                <Form.Input
                  field="scheduleReference"
                  inline
                  disabled={!isInternalUser}
                  fluid
                />
              </div>
            </div>
          )}
          {!process.env.TRADE_AS_IPS && (
            <div className="float-left flex grid-col">
              <div className="spaced not-bold">
                {t("ScheduleRow.RevisionNumber")}
              </div>
              <div className="float-left spaced no-padding-left">
                <Form.Input
                  field="revisionNumber"
                  inline
                  disabled={!isInternalUser}
                  fluid
                />
              </div>
            </div>
          )}
        </div>

        <div className="float-left">
          <div>
            <div className="float-left spaced not-bold">
              {t("ScheduleRow.Schedule_Agreement_Date")}
            </div>
            <div className="float-left no-padding-left spaced schedule-agreement-date-form-field">
              <Form.Date
                local
                field="startDate"
                placeholder=""
                inline
                disabled={
                  !isAdminUser ||
                  process.env.TRADE_AS_IPS ||
                  formProps?.value?.isLive
                }
              />
            </div>
          </div>
          <div className="float-right">
            <div className="float-left spaced not-bold red">
              {t("ScheduleRow.Credit_Available")}
            </div>
            <div className="float-left no-padding-left spaced">
              <FormattedNumericInput
                field="creditAvailable"
                disabled={
                  !canUpdateCreditAvailable ||
                  (process.env.TRADE_AS_IPS && !isAdminUser)
                }
                decimalPlaces={2}
                subLabel={process.env.MAJOR_CURRENCY}
              />
            </div>
          </div>
        </div>
        <div className="float-left">
          <div className="flex spaced">
            <PermissionCheck target={Targets.Schedules} action={Actions.Edit}>
              <ActionButton
                type="save"
                floated="right"
                onClick={() => formEvents.onSubmit()}
                loading={state.submitting}
                className={"action-button" + (state.dirty ? " " : " hidden")}
              />
              <ActionButton
                type="cancel"
                floated="right"
                onClick={() => formEvents.onReset()}
                loading={state.submitting}
                className={"action-button" + (state.dirty ? " " : " hidden")}
              />
            </PermissionCheck>
          </div>
        </div>
        <DeleteSchedule
          scheduleId={schedule.scheduleId}
          scheduleReference={schedule.scheduleReference}
          onSubmitted={reloadAgreement}
        />

        <div className="float-left spaced">
          <PermissionCheck target={Targets.Sheet} action={Actions.Create}>
            {canDuplicateFromTemplates && hasTemplates ? (
              <DuplicateFromTemplateModal
                agreement={agreement}
                schedule={schedule}
                companyFilters={companyFilters}
              />
            ) : (
              <TriggerBuilder
                submitAction={generateSheet}
                submitParams={{
                  clientId: agreement.clientId,
                  dealerOfficeId: companyFilters.dealerOffice.id,
                  scheduleId: schedule.scheduleId,
                }}
                onSubmitted={(submitResponse) =>
                  history.push(SheetRoutes.Sheet(submitResponse.sheetId), {
                    isNewSheet: true,
                  })
                }
                renderTrigger={(fireTrigger, triggerState) => (
                  <div>
                    <AddButton
                      className="clear-bg"
                      content={t("ScheduleRow.New_Sheet")}
                      icon={BlueAddSvg}
                      onClick={fireTrigger}
                      loading={triggerState.submitting}
                    />
                    {triggerState.success && triggerState.data && (
                      <span>{triggerState.data.message}</span>
                    )}
                  </div>
                )}
              />
            )}
          </PermissionCheck>
        </div>
        <div className="clear"></div>
      </div>
    </Form>
  );
};

const ScheduleRowForm = ({
  agreement,
  schedule,
  isAdminUser,
  isInternalUser,
  getExpandIconName,
  hasPermission,
  overrideSalesRequestRestrictions,
}) => {
  const history = useHistory();
  const { t } = useTranslation("agreements");

  const canUpdateCreditAvailable = hasPermission(
    Targets.Schedules,
    Actions.UpdateCreditAvailable,
  );
  const canDuplicateFromTemplates = hasPermission(
    Targets.ClientTemplate,
    Actions.DuplicateFromTemplate,
  );
  const {
    expandedAgreementsAndSchedules,
    filters,
    companyFilters,
    hasTemplates,
  } = useSelector(getAgreementsState);
  const dispatch = useDispatch();

  const onToggleScheduleExpanded = (id) => {
    if (expandedAgreementsAndSchedules?.includes(id)) {
      dispatch(removeExpandedAgreementOrSchedule(id));
    } else {
      dispatch(addExpandedAgreementOrSchedule(id));
      dispatch(getAgreementScheduleSheets(id, filters));
    }
  };

  const reloadAgreement = () => {
    dispatch(getMasterAgreement(agreement.agreementId, filters));
  };

  const reloadSheets = () => {
    dispatch(getAgreementScheduleSheets(schedule.scheduleId, filters));
  };

  return (
    <FormBuilder
      initialData={schedule}
      submitAction={updateSchedule}
      submitParams={{ id: schedule.scheduleId }}
      onSubmitted={() => {
        reloadAgreement();
        reloadSheets();
      }}
      renderForm={(formProps, state, formEvents) => (
        <ScheduleFormContent
          formProps={formProps}
          state={state}
          formEvents={formEvents}
          agreement={agreement}
          schedule={schedule}
          isAdminUser={isAdminUser}
          isInternalUser={isInternalUser}
          getExpandIconName={getExpandIconName}
          canUpdateCreditAvailable={canUpdateCreditAvailable}
          canDuplicateFromTemplates={canDuplicateFromTemplates}
          companyFilters={companyFilters}
          t={t}
          history={history}
          onToggleScheduleExpanded={onToggleScheduleExpanded}
          reloadAgreement={reloadAgreement}
          overrideSalesRequestRestrictions={overrideSalesRequestRestrictions}
          hasTemplates={hasTemplates}
        />
      )}
    />
  );
};

export default compose(withCurrentUser, withPermissions)(ScheduleRowForm);
